import { useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components";
import logo from "../../../assets/logo_report.png";
import { globalFormatCurrencyBRL, globalMaskDocument, globalTranslateStatusComission, globalTranslateTypeComission } from "../../../shared/utils";
import { format } from "date-fns";
import { exportExcelStyleWithResume } from "../utils/exportExcelWithResume";

export const useExportComissionExcel = (setIsLoading: (v: boolean) => void) => {
	const toastHandler = useToast();

	// Função buildSummary dentro do hook
	const buildSummary = (items: any[]) => {
		const validItems = items.filter((i) => !["paid", "suspended", "canceled"].includes(i.status));

		const types = ["prize", "bonus", "discount"];
		const resumo = types.map((type) => {
			const label = type === "prize" ? "Premiação" : type === "bonus" ? "Bonificação" : "Estorno";
			const filtered = validItems.filter((i) => i.type === type);
			const total = filtered.reduce((acc, i) => acc + Number(i.value), 0);
			return {
				label,
				totalValue: total,
				totalFormatted: `R$ ${total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
			};
		});

		const totalPremiacao = resumo.find((r) => r.label === "Premiação")?.totalValue ?? 0;
		const totalBonificacao = resumo.find((r) => r.label === "Bonificação")?.totalValue ?? 0;
		const totalEstorno = resumo.find((r) => r.label === "Estorno")?.totalValue ?? 0;

		const saldoAPagar = totalPremiacao + totalBonificacao - totalEstorno;

		const totalPagos = items.filter((i) => i.status === "paid").reduce((acc, i) => acc + Number(i.value), 0);

		const totalCanceladosSuspensos = items.filter((i) => ["suspended", "canceled"].includes(i.status)).reduce((acc, i) => acc + Number(i.value), 0);

		return {
			resumo,
			saldoAPagar,
			totalPagos,
			totalCanceladosSuspensos,
		};
	};

	const exportComissionToExcel = async (items: any[], clientName: string) => {
		try {
			setIsLoading(true);

			if (!items.length) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Nenhum dado disponível para exportar." />,
				});
				return;
			}

			const dataRows = items.map((item) => ({
				certificate: item.proposalCertificateNumber ?? "-",
				name: item.proposalName ?? "Cliente sem nome",
				document: globalMaskDocument(item.proposalDocument),
				type: globalTranslateTypeComission(item.type),
				value: globalFormatCurrencyBRL(item.value),
				due_date: format(new Date(item.dueDate), "dd/MM/yyyy"),
				status: globalTranslateStatusComission(item.status),
				prize: item.installmentNumber ? `${item.installmentNumber} / ${item.proposalRentMonthsCount}` : "-",
				value_amount: globalFormatCurrencyBRL(item.installmentValue),
				indice: item.proposalCommissionValue ? item.proposalCommissionValue.toString() : "-",
			}));

			// Chamar buildSummary com items ORIGINAIS (não os formatados)
			const { resumo, saldoAPagar, totalPagos, totalCanceladosSuspensos } = buildSummary(items);

			await exportExcelStyleWithResume({
				title: "Relatório Financeiro de Premiações/Bonificações",
				subtitle: `Imobiliária: ${clientName}`,
				data: dataRows,
				columns: [
					{ key: "certificate", label: "Certificado" },
					{ key: "name", label: "Nome" },
					{ key: "document", label: "Documento" },
					{ key: "type", label: "Tipo" },
					{ key: "prize", label: "Parcela" },
					{ key: "value_amount", label: "Valor Parcela" },
					{ key: "indice", label: "Índice" },
					{ key: "value", label: "Valor" },
					{ key: "due_date", label: "Vencimento" },
					{ key: "status", label: "Status" },
				],
				logoBase64: logo,
				fileName: `relatorio-comissoes-${clientName}.xlsx`,
				// Passa o resumoPremiacao completo
				resumoPremiacao: {
					resumo,
					saldoAPagar,
					totalPagos,
					totalCanceladosSuspensos,
				},
			});
		} catch (err) {
			console.error(err);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Falha ao exportar o Excel." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return { exportComissionToExcel };
};
